import { Mail } from 'lucide-react';
import { OnboardingChecklistCard } from '../../../../components/onboarding/OnboardingChecklistCard';
import { usePersonOnboarding } from '../../../../hooks/usePersonOnboarding';
import { EmptyState } from '../../../../components/ui/EmptyState';

/**
 * Step 7. Last look at what the person will actually see when they sign in,
 * and the decision to send the welcome email now. Sending is ticked by
 * default: an onboarding record nobody has been told about is the common
 * failure of doing this by hand.
 */
export function ReviewStep({
  personId,
  personName,
  sendWelcome,
  onSendWelcomeChange,
}: {
  personId: string;
  personName: string | null;
  sendWelcome: boolean;
  onSendWelcomeChange: (next: boolean) => void;
}) {
  const onboarding = usePersonOnboarding(personId);
  // A failed fetch also leaves data undefined; only call it empty once the
  // query has actually answered, or a blip reads as "nothing was assigned".
  const assignedNothing = !!onboarding.data && onboarding.data.total === 0;

  return (
    <div className="space-y-4">
      {assignedNothing ? (
        <EmptyState
          title="Nothing assigned yet"
          description="This person has no onboarding items. Go back and pick their training, documents, forms or tasks, or finish now and add them later from their record."
        />
      ) : (
        <OnboardingChecklistCard personId={personId} />
      )}

      <label className="flex items-start gap-3 rounded-token border border-line/10 bg-surface-2 px-4 py-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={sendWelcome}
          onChange={(e) => onSendWelcomeChange(e.target.checked)}
        />
        <span>
          <span className="flex items-center gap-1.5 font-semibold text-fg">
            <Mail className="h-4 w-4 text-fg-muted" />
            Send the welcome email now
          </span>
          <span className="mt-1 block text-xs text-fg-muted">
            Emails {personName ?? 'them'} their sign-in link so they can start
            working through the list. Goes to their personal email if one was
            given, otherwise their work address. You can resend it any time from
            the onboarding dashboard.
          </span>
        </span>
      </label>
    </div>
  );
}
